import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Send, Plus, Trash2, Sparkles, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const SUGGESTIONS = ["Quale stazione ha il miglior km/l negli ultimi 30 giorni?", "Confronta il costo al km netto delle stazioni mese per mese",
  "Quanto abbiamo speso in franchigie e quali stazioni hanno più sinistri aperti?", "Come stanno i rimborsi Amazon rispetto alla spesa carburante?"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "whitespace-pre-wrap bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-800 [&_li]:ml-4 [&_ol]:list-decimal [&_p]:my-1 [&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:bg-slate-50 [&_th]:px-2 [&_th]:py-1 [&_ul]:list-disc"}`} data-testid={`ai-msg-${m.role}`}>
      {!m.content ? <span className="animate-pulse text-slate-400">Sto analizzando i dati...</span> : m.role === "user" ? m.content : <div className="overflow-x-auto"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>}
    </div>
  </div>
);

export default function AiAssistantPage() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const loadSessions = () => api.get("/ai/sessions").then((r) => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const open = async (id) => { setSid(id); const r = await api.get(`/ai/sessions/${id}`); setMsgs(r.data); };
  const remove = async (id) => { await api.delete(`/ai/sessions/${id}`); if (id === sid) { setSid(null); setMsgs([]); } loadSessions(); };

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("fleet_token")}` },
        body: JSON.stringify({ session_id: sid, message }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Errore assistente");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const l of lines) {
          const p = l.replace(/^data: /, "");
          if (p === "[DONE]") continue;
          const ev = JSON.parse(p);
          if (ev.session_id) setSid(ev.session_id);
          if (ev.error) toast.error(ev.error);
          if (ev.delta) setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + ev.delta }]);
        }
      }
      loadSessions();
    } catch (e) { toast.error(errMsg(e)); setMsgs((m) => m.slice(0, -1)); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Assistente AI" subtitle="Fai domande sui dati di carburante, flotta, sinistri, noleggi e Telepass. Risponde Gemini 3.1 Pro.">
        <Button variant="outline" onClick={() => { setSid(null); setMsgs([]); }} data-testid="ai-new-chat"><Plus className="mr-2 h-4 w-4" /> Nuova conversazione</Button>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <aside className="hidden max-h-[65vh] space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 lg:block" data-testid="ai-sessions">
          {sessions.map((s) => (
            <div key={s.id} className={`group flex items-center gap-2 rounded-lg px-2 py-2 text-sm ${s.id === sid ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <button className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => open(s.id)} data-testid={`ai-session-${s.id}`}><MessageSquare className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{s.title}</span></button>
              <button className="text-slate-400 hover:text-rose-600" onClick={() => remove(s.id)} data-testid={`ai-session-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!sessions.length && <p className="p-3 text-xs text-slate-400">Nessuna conversazione.</p>}
        </aside>
        <div className="flex h-[65vh] flex-col rounded-xl border border-slate-200 bg-slate-50">
          <div className="flex gap-2 border-b border-slate-200 bg-white p-2 lg:hidden" data-testid="ai-sessions-mobile">
            <Select value={sid || undefined} onValueChange={open} disabled={!sessions.length}>
              <SelectTrigger className="min-w-0 flex-1" data-testid="ai-sessions-select"><SelectValue placeholder={sessions.length ? `Conversazioni precedenti (${sessions.length})` : "Nessuna conversazione"} /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`ai-sessions-opt-${s.id}`}><span className="block max-w-[260px] truncate">{s.title}</span></SelectItem>)}</SelectContent>
            </Select>
            {sid && <Button size="icon" variant="ghost" className="text-slate-500 hover:text-rose-600" onClick={() => window.confirm("Eliminare questa conversazione?") && remove(sid)} data-testid="ai-session-delete-mobile"><Trash2 className="h-4 w-4" /></Button>}
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ai-messages">
            {!msgs.length && (
              <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                <Sparkles className="h-8 w-8 text-amber-500" />
                <p className="text-sm text-slate-500">Prova a chiedere:</p>
                <div className="grid w-full max-w-2xl gap-2 sm:grid-cols-2">
                  {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:border-slate-400" data-testid={`ai-suggestion-${i}`}>{s}</button>)}
                </div>
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={end} />
          </div>
          <div className="flex gap-2 border-t border-slate-200 bg-white p-3">
            <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una domanda..." className="min-h-[40px] resize-none"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="ai-input" />
            <Button onClick={() => send()} disabled={busy || !text.trim()} data-testid="ai-send-button"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
